import {
  FiCopy,
  FiFlag,
  FiMoreVertical,
  FiThumbsDown,
  FiThumbsUp,
} from "react-icons/fi";
import { LiaUndoAltSolid } from "react-icons/lia";
import styled from "styled-components";
import FlexBox from "../FlexBox";
import {
  BRICK_TERRACOTA_800,
  DAVYS_GREY_200,
  DAVYS_GREY_400,
  DAVYS_GREY_600,
  DAVYS_GREY_700,
  DAVYS_GREY_800,
  white,
} from "../colors";
import ReportBottomDrawer from "./ReportBottomDrawer";
import useMobileView from "../../../hooks/useMobileView";
import Liked from "../../../assets/AiChat/liked.svg";
import Disliked from "../../../assets/AiChat/disliked.svg";
const MessageBubbleFooter = styled(FlexBox)`
  width: 100%;
  padding: 0.5rem 1rem;
  align-items: center;
  justify-content: space-between;
`;

const FooterActions = styled(FlexBox)`
  align-items: center;
  column-gap: 0.25rem;
`;

const FooterActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: ${DAVYS_GREY_700};
  cursor: pointer;
  transition:
    background 0.2s ease,
    transform 0.2s ease;

  @media (hover: hover) {
    &:hover:not(:disabled) {
      background: ${DAVYS_GREY_200};
      transform: translateY(-2px);
    }
  }

  &:focus-visible {
    outline: 2px solid ${BRICK_TERRACOTA_800};
    outline-offset: 2px;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const MenuButton = styled(FooterActionButton)`
  position: relative;
`;

const MenuWrap = styled(FlexBox)`
  position: relative;
`;

const MenuDropdown = styled(FlexBox)`
  position: absolute;
  bottom: calc(100% + 0.375rem);
  right: 0;
  z-index: 10;
  width: max-content;
  border: 1px solid ${DAVYS_GREY_400};
  border-radius: 0.5rem;
  background: ${white};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  padding: 0.25rem;
  flex-direction: column;
`;

const MenuItem = styled.button`
  width: 100%;
  display: inline-flex;
  align-items: center;
  column-gap: 0.5rem;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  color: ${DAVYS_GREY_800};
  font-size: 0.8125rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;

  &:hover {
    background: ${DAVYS_GREY_200};
  }
`;

const MenuStatusText = styled.span`
  padding: 0.5rem 0.75rem;
  color: ${DAVYS_GREY_600};
  font-size: 0.8125rem;
  white-space: nowrap;
`;

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;

  @media (hover: hover) {
    &:hover .footer-tooltip {
      opacity: 1;
      visibility: visible;
      transform: translate(-50%, -8px);
    }
  }
`;

const Tooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 12px);
  left: 50%;
  transform: translate(-50%, 0);
  opacity: 0;
  visibility: hidden;
  transition: all 0.18s ease;
  pointer-events: none;
  z-index: 999;

  background: ${DAVYS_GREY_700};
  color: ${white};
  padding: 0.75rem 0.875rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  line-height: 1;
  white-space: nowrap;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-width: 8px 8px 0;
    border-style: solid;
    border-color: ${DAVYS_GREY_700} transparent transparent transparent;
  }
`;

const AiChatMessageFooter = ({
  message,
  messageId,
  isLastMessage,
  lastMessageIsAi,
  isMenuOpen,
  isReported,
  menuRef,
  onCopy,
  onRegenerate,
  onLike,
  onDislike,
  onToggleMenu,
  onReport,
}) => {
  const isMobile = useMobileView();

  const isLiked = message?.feedback === "like";
  const isDisliked = message?.feedback === "dislike";

  const handleActionClick = (e, callbackAction) => {
    if (callbackAction) {
      callbackAction(message, messageId);
    }
    e.currentTarget.blur();
  };

  return (
    <MessageBubbleFooter>
      <FooterActions>
        <TooltipWrapper>
          <FooterActionButton onClick={e => handleActionClick(e, onCopy)}>
            <FiCopy size={18} />
          </FooterActionButton>
          <Tooltip className="footer-tooltip">Copy text</Tooltip>
        </TooltipWrapper>

        {isLastMessage && lastMessageIsAi && (
          <TooltipWrapper>
            <FooterActionButton
              onClick={e => handleActionClick(e, onRegenerate)}
            >
              <LiaUndoAltSolid size={18} />
            </FooterActionButton>
            <Tooltip className="footer-tooltip">Regenerate response</Tooltip>
          </TooltipWrapper>
        )}

        <TooltipWrapper>
          <FooterActionButton
            onClick={e => handleActionClick(e, onLike)}
            disabled={isDisliked}
          >
            {isLiked ? (
              <img src={Liked} alt="Liked" />
            ) : (
              <FiThumbsUp size={18} />
            )}
          </FooterActionButton>
          <Tooltip className="footer-tooltip">
            {isDisliked
              ? "Remove dislike to like this response"
              : "I like the response"}
          </Tooltip>
        </TooltipWrapper>

        <TooltipWrapper>
          <FooterActionButton
            onClick={e => handleActionClick(e, onDislike)}
            disabled={isLiked}
          >
            {isDisliked ? (
              <img src={Disliked} alt="Disliked" />
            ) : (
              <FiThumbsDown size={18} />
            )}
          </FooterActionButton>
          <Tooltip className="footer-tooltip">
            {isLiked
              ? "Remove like to dislike this response"
              : "I don't like the response"}
          </Tooltip>
        </TooltipWrapper>
      </FooterActions>

      <MenuWrap ref={menuRef}>
        <MenuButton onClick={() => onToggleMenu(messageId)}>
          <FiMoreVertical size={18} />
        </MenuButton>

        {isMenuOpen &&
          (isMobile ? (
            <ReportBottomDrawer
              isReported={isReported}
              onClose={() => onToggleMenu(null)}
              onReport={() => onReport?.(message, messageId)}
            />
          ) : isReported ? (
            <MenuDropdown>
              <MenuStatusText>Already reported</MenuStatusText>
            </MenuDropdown>
          ) : (
            <MenuDropdown>
              <MenuItem onClick={() => onReport?.(message, messageId)}>
                <FiFlag size={16} />
                Report
              </MenuItem>
            </MenuDropdown>
          ))}
      </MenuWrap>
    </MessageBubbleFooter>
  );
};

export default AiChatMessageFooter;
